import React from 'react';
import { AbsoluteFill, interpolate, Sequence, useCurrentFrame } from 'remotion';
import type { ProjectModel, TekstItem } from '@video-studio/shared';

/**
 * Tekstlaag.
 *
 * Positie is relatief (0..1 van het kader), zodat dezelfde tekstlaag in 16:9,
 * 9:16 en 1:1 op dezelfde plek blijft staan. De fontgrootte is in pixels op de
 * compositiehoogte, dus niet schaalafhankelijk van de preview.
 *
 * Er worden bewust alleen systeemlettertypen en lokaal geladen lettertypen
 * gebruikt. Daardoor werkt een export ook zonder internetverbinding.
 */
export interface TekstLaagProps {
  item: TekstItem;
  project: ProjectModel;
}

/** Onze Nederlandse uitlijningswaarde naar de CSS-waarde. */
export function cssUitlijning(uitlijning: 'links' | 'midden' | 'rechts'): 'left' | 'center' | 'right' {
  if (uitlijning === 'links') return 'left';
  if (uitlijning === 'rechts') return 'right';
  return 'center';
}

export const TekstLaag: React.FC<TekstLaagProps> = ({ item, project }) => {
  return (
    <Sequence
      from={Math.max(0, Math.round(item.startFrame))}
      durationInFrames={Math.max(1, Math.round(item.duurInFrames))}
      name={item.naam || 'Tekst'}
    >
      <TekstInSequence item={item} project={project} />
    </Sequence>
  );
};

const TekstInSequence: React.FC<{ item: TekstItem; project: ProjectModel }> = ({ item, project }) => {
  const frame = useCurrentFrame();

  let opaciteit = item.dekking;
  let extraY = 0;
  if (item.animatie === 'vervagen') {
    opaciteit *= interpolate(frame, [0, Math.min(12, item.duurInFrames / 2)], [0, 1], {
      extrapolateLeft: 'clamp',
      extrapolateRight: 'clamp',
    });
  }
  if (item.animatie === 'omhoog') {
    opaciteit *= interpolate(frame, [0, Math.min(15, item.duurInFrames / 2)], [0, 1], {
      extrapolateLeft: 'clamp',
      extrapolateRight: 'clamp',
    });
    extraY = interpolate(frame, [0, Math.min(15, item.duurInFrames / 2)], [24, 0], {
      extrapolateLeft: 'clamp',
      extrapolateRight: 'clamp',
    });
  }

  const achtergrond = item.achtergrondKleur === 'transparant' ? 'transparent' : item.achtergrondKleur;

  return (
    <AbsoluteFill
      style={{
        justifyContent: 'center',
        alignItems: 'center',
        // Het hele kader is het positioneringsvlak; x/y zijn fracties daarvan.
        padding: 0,
      }}
    >
      <div
        style={{
          position: 'absolute',
          left: `${item.x * 100}%`,
          top: `${item.y * 100}%`,
          transform: `translate(-50%, -50%) translateY(${extraY}px) rotate(${item.rotatie}deg)`,
          width: `${item.maxBreedte * 100}%`,
          display: 'flex',
          justifyContent:
            item.uitlijning === 'links' ? 'flex-start' : item.uitlijning === 'rechts' ? 'flex-end' : 'center',
        }}
      >
        <span
          style={{
            display: 'inline-block',
            fontFamily: item.fontFamily || 'Inter',
            fontSize: item.fontSize,
            fontWeight: item.fontWeight,
            color: item.kleur,
            textAlign: cssUitlijning(item.uitlijning),
            lineHeight: 1.2,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            backgroundColor: achtergrond,
            padding: achtergrond === 'transparent' ? 0 : '0.15em 0.4em',
            borderRadius: 8,
            opacity: opaciteit,
            textShadow: item.schaduw ? '0 2px 12px rgba(0,0,0,0.65)' : undefined,
          }}
        >
          {item.tekst}
        </span>
      </div>
    </AbsoluteFill>
  );
};
